/**
 * Compresses an image to WebP format on the client side
 * maintaining the original resolution but optimizing the file size.
 *
 * @param file The original File object (JPEG, PNG, etc.)
 * @param quality Quality from 0.0 to 1.0 (default 0.8)
 * @returns A Promise that resolves to the new WebP File object
 */
export const compressImageToWebP = (file: File, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                // Create a canvas with the same dimensions as the original image
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return reject(new Error('Failed to get canvas context'));
                }

                // Draw the original image onto the canvas
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                // Convert canvas to WebP Blob
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            return reject(new Error('Canvas to Blob conversion failed'));
                        }

                        // Create a new File object from the Blob
                        const originalName = file.name.split('.')[0];
                        const webpFile = new File([blob], `${originalName}.webp`, {
                            type: 'image/webp',
                            lastModified: Date.now(),
                        });

                        resolve(webpFile);
                    },
                    'image/webp',
                    quality
                );
            };

            img.onerror = () => reject(new Error('Failed to load image for compression'));
            img.src = event.target?.result as string;
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};
